// 2026-06-15 — translates the engineer-facing `ExpenditureCategory` enum into
// treasurer-first display labels at the render boundary (the triage-ledger
// category dropdown). Sibling of `jurisdictionLabels` / `preflightCopy`: a pure,
// React-free map so it can be reused across packages and snapshot-tested
// cheaply. Per `feedback_treasurer_first_copy.md`, the compound PascalCase
// identifier (`ContributionRefund`) must never render verbatim; single-word
// identifiers that already read as plain English (`Operating`, `Travel`) keep
// their word.

import { ExpenditureCategory } from '../generated/enums.gen';

/**
 * Per-category treasurer-first label. Exported so the snapshot test can pin the
 * literals; a new `ExpenditureCategory` member without an entry surfaces as a
 * compile error (the `Record` is keyed by the enum).
 */
export const EXPENDITURE_CATEGORY_LABELS: Readonly<
  Record<ExpenditureCategory, string>
> = {
  [ExpenditureCategory.Operating]: 'Operating',
  [ExpenditureCategory.Fundraising]: 'Fundraising',
  [ExpenditureCategory.Travel]: 'Travel',
  [ExpenditureCategory.Personnel]: 'Personnel',
  [ExpenditureCategory.Advertising]: 'Advertising',
  [ExpenditureCategory.Reimbursement]: 'Reimbursement',
  // "to another committee" disambiguates from an own-account transfer, which is
  // never an expenditure (that path is Exclude-from-report, ADR-014).
  [ExpenditureCategory.Transfer]: 'Transfer to another committee',
  [ExpenditureCategory.Other]: 'Other',
  [ExpenditureCategory.LoanRepayment]: 'Loan repayment',
  [ExpenditureCategory.OtherDisbursement]: 'Other disbursement',
  // The import placeholder — surfaced so an imported, not-yet-classified expense reads
  // as needing attention (never treasurer-selectable; excluded from the picker).
  [ExpenditureCategory.Uncategorized]: 'Uncategorized',
};
// The ContributionRefund category was retired — a refund is now a
// ContributionRefund entity, never an Expenditure category — so there is no
// label entry for it (the Record stays exhaustive over the remaining values).

/**
 * Alabama-specific label overrides. The AL FCPA v6.0 code table has NO "transfer"
 * or "reimbursement" purpose code, so the AL builder files both `Transfer` and
 * `Reimbursement` as ExPurpose "Other" (10). The default federal wording ("Transfer
 * to another committee") implies inter-committee mechanics AL doesn't model, and a
 * distinct "Reimbursement" concept AL doesn't have — so the AL treasurer sees labels
 * that reflect the actual filing behavior. Only the categories that differ are listed.
 */
const ALABAMA_CATEGORY_LABEL_OVERRIDES: Partial<
  Record<ExpenditureCategory, string>
> = {
  [ExpenditureCategory.Transfer]: 'Transfer',
  [ExpenditureCategory.Reimbursement]: 'Reimbursement / Other',
};

/**
 * Resolves the treasurer-first display label for an `ExpenditureCategory`, keyed by
 * the campaign's ruleset (the compliance profile's `stateCode` or the settings'
 * `rulesetCode` — both `"AL"` for Alabama, compared case-insensitively, matching
 * `contributionDesignationOptions`). Alabama gets the portal-accurate labels above;
 * every other or omitted ruleset keeps the default federal-flavored labels. Falls
 * back to the raw value for any unexpected (non-enum) input so the boundary never
 * renders empty.
 */
export function expenditureCategoryLabel(
  category: ExpenditureCategory,
  rulesetCode?: string | null,
): string {
  if ((rulesetCode ?? '').toUpperCase() === 'AL') {
    const override = ALABAMA_CATEGORY_LABEL_OVERRIDES[category];
    if (override) return override;
  }
  return EXPENDITURE_CATEGORY_LABELS[category] ?? category;
}
